import { z } from 'zod';
/**
 * Контракты эндпоинтов `modules/link-rules` (часть рефакторинга
 * «cross_link-поля → link_rules + первоклассные item_links»).
 *
 * LinkRule — конфиг авто-связывания на уровне item_type'а.
 * Заменяет cross_link-поле:
 *  - matchRules — те же условия, что раньше жили в config.matchRules
 *    cross_link-поля (формат идентичен, миграция данных переносит без
 *    изменений)
 *  - linkType — тип Jira-связи, аналог config.jiraLink.type
 *  - pushEnabled — управляет, отправлять ли связь в Jira (страховка от
 *    echo-push'а связей, пришедших из Jira «незнакомого» типа)
 */
// ─── matchRules (формат совместим с прежним cross_link.matchRules) ──────
export const MatchRuleModeSchema = z.enum(['eq', 'any_in', 'in_any', 'dict_lookup']);
/**
 * Направление Jira-связи при push:
 *  - outward — «текущая запись → цель» (напр. «Включает в себя»)
 *  - inward  — «текущая запись ← цель» (напр. «Включена в»)
 * linkType хранит каноничное имя типа Jira (напр. «Включение»), а направление
 * выбирает, какую из его подписей увидит Jira при createIssueLink.
 */
export const JiraLinkDirectionSchema = z.enum(['outward', 'inward']);
export const MatchRuleSchema = z.object({
    sourceField: z.string().min(1),
    targetField: z.string().min(1),
    required: z.boolean().optional(),
    mode: MatchRuleModeSchema.optional(),
    /** Для mode='dict_lookup' — slug колонки в справочнике */
    lookupColumn: z.string().optional(),
});
// ─── LinkRule DTO ──────────────────────────────────────────────────────
export const LinkRuleSchema = z.object({
    id: z.string().uuid(),
    itemTypeId: z.string().uuid(),
    targetItemTypeId: z.string().uuid(),
    name: z.string().min(1).max(200),
    linkType: z.string().min(1).max(100),
    jiraLinkDirection: JiraLinkDirectionSchema,
    matchRules: z.array(MatchRuleSchema),
    pushEnabled: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ─── POST /api/spaces/:id/item-types/:itemTypeId/link-rules ────────────
export const CreateLinkRuleRequestSchema = z.object({
    targetItemTypeId: z.string().uuid(),
    name: z.string().trim().min(1).max(200),
    linkType: z.string().trim().min(1).max(100).default('Relates'),
    jiraLinkDirection: JiraLinkDirectionSchema.optional().default('outward'),
    matchRules: z.array(MatchRuleSchema).optional().default([]),
    pushEnabled: z.boolean().optional().default(true),
});
// ─── PATCH /api/spaces/:id/item-types/:itemTypeId/link-rules/:ruleId ──
export const UpdateLinkRuleRequestSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    linkType: z.string().trim().min(1).max(100).optional(),
    jiraLinkDirection: JiraLinkDirectionSchema.optional(),
    matchRules: z.array(MatchRuleSchema).optional(),
    pushEnabled: z.boolean().optional(),
});
// ─── POST /api/spaces/:id/item-types/:itemTypeId/link-rules/:ruleId/apply-bulk ──
export const ApplyLinkRuleBulkRequestSchema = z.object({
    /** Если пусто/не задано — правило применяется ко всем записям source-типа в пространстве. */
    itemIds: z.array(z.string().uuid()).optional(),
});
export const ApplyLinkRuleBulkResponseSchema = z.object({
    itemsProcessed: z.number().int().nonnegative(),
    linksCreated: z.number().int().nonnegative(),
});
